import { useState, useEffect } from "react";

// ─── FONTS ────────────────────────────────────────────────────────────────────

function FontLoader() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Playfair+Display:wght@600;700;800;900&display=swap');
      html { scroll-behavior: smooth; }
    `}</style>
  );
}

// ─── SHARED STYLES ────────────────────────────────────────────────────────────

const heading = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontWeight: 800,
  lineHeight: 1.2,
};

const body = {
  fontFamily: "'Inter', system-ui, sans-serif",
  lineHeight: 1.7,
};

const sectionPad = { padding: "100px 24px", maxWidth: 1120, margin: "0 auto" };
const sectionPadNarrow = { padding: "100px 24px", maxWidth: 960, margin: "0 auto" };

// ─── REFERRAL HELPERS ────────────────────────────────────────────────────────

function generateReferralCode(firstName, lastName) {
  const first = firstName.toLowerCase().trim().replace(/[^a-z]/g, '');
  const lastInitial = lastName.toLowerCase().trim().charAt(0).replace(/[^a-z]/g, '');
  const random = Math.floor(1000 + Math.random() * 9000);
  return `${first}-${lastInitial}-${random}`;
}

// ─── RESPONSIVE HELPER ───────────────────────────────────────────────────────

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return mobile;
}

// ─── HERO ─────────────────────────────────────────────────────────────────────

function Hero() {
  const mobile = useIsMobile();
  return (
    <section style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg, #1C1917 0%, #292524 60%, #14532d 100%)",
      display: "flex", flexDirection: "column",
    }}>
      {/* Wordmark */}
      <div style={{ padding: "28px 32px", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 22 }}>🌱</span>
        <span style={{ ...heading, fontSize: 19, color: "#fff", fontWeight: 700 }}>SproutCode</span>
      </div>

      {/* Center content */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 24px", textAlign: "center" }}>
        <p style={{
          ...body,
          fontSize: mobile ? 13 : 15,
          color: "#F59E0B",
          fontWeight: 500,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          margin: "0 0 14px",
        }}>
          Conscious childhood development for the AI generation
        </p>
        <h1 style={{
          ...heading,
          fontSize: mobile ? 38 : 56,
          color: "#fff",
          margin: "0 0 24px",
          maxWidth: 760,
          letterSpacing: -0.5,
        }}>
          The only platform where AI cannot think for your child
        </h1>
        <p style={{
          ...body,
          fontSize: mobile ? 16 : 18,
          color: "#A8A29E",
          maxWidth: 620,
          margin: "0 0 40px",
        }}>
          Where conscious parents introduce their kids to the technology that will define their lives, without surrendering the things they are trying to protect.
        </p>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center", marginBottom: 52 }}>
          <a href="#waitlist" style={{
            ...body,
            background: "#F59E0B", color: "#1C1917",
            border: "none", borderRadius: 12, padding: "15px 32px",
            fontSize: 16, fontWeight: 700, cursor: "pointer",
            textDecoration: "none", display: "inline-block",
          }}>
            Join the waitlist
          </a>
          <a href="#how-it-works" style={{
            ...body,
            background: "transparent", color: "#fff",
            border: "2px solid rgba(255,255,255,0.5)", borderRadius: 12,
            padding: "15px 32px", fontSize: 16, fontWeight: 600,
            cursor: "pointer", textDecoration: "none", display: "inline-block",
          }}>
            See how it works
          </a>
        </div>

        {/* Trust badges */}
        <div style={{
          display: "flex", gap: mobile ? 8 : 14, flexWrap: "wrap", justifyContent: "center",
        }}>
          {["Audio for pre-readers", "Brain-First AI", "Offline Activities", "Parent Dashboard"].map(label => (
            <span key={label} style={{
              ...body,
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 99, padding: "6px 18px",
              fontSize: 13, color: "rgba(255,255,255,0.6)",
              fontWeight: 500,
            }}>
              {label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PROBLEM ──────────────────────────────────────────────────────────────────

const problemCards = [
  {
    title: "AI is doing kids' homework",
    text: "ChatGPT, Copilot, Gemini. None of them stop your child from asking for the answer. Passive consumption disguised as learning.",
  },
  {
    title: "Screen time has no real solution",
    text: "Every parent knows it is a problem. Nobody has built a platform that takes it seriously from the ground up.",
  },
  {
    title: "Nothing is built for what childhood actually looks like now",
    text: "Coding platforms predate AI. Wellbeing apps stop at meditation. Conscious tech for school-age kids does not exist as a category. Parents are left assembling answers from products that were not designed for the world they are raising kids in.",
  },
];

function Problem() {
  const mobile = useIsMobile();
  return (
    <section style={{ background: "#FFFBEB" }}>
      <div style={sectionPad}>
        <h2 style={{ ...heading, fontSize: mobile ? 28 : 38, color: "#1C1917", textAlign: "center", margin: "0 0 52px" }}>
          Three things happening right now in every household
        </h2>
        <div style={{
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "repeat(3, 1fr)",
          gap: 24,
        }}>
          {problemCards.map(card => (
            <div key={card.title} style={{
              background: "#fff",
              borderRadius: 16, padding: "32px 28px",
              boxShadow: "0 2px 20px rgba(0,0,0,0.05)",
              border: "1px solid #E7E5E4",
            }}>
              <h3 style={{ ...heading, fontSize: 20, color: "#1C1917", margin: "0 0 14px", fontWeight: 700 }}>
                {card.title}
              </h3>
              <p style={{ ...body, fontSize: 15, color: "#57534E", margin: 0 }}>
                {card.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── SOLUTION ─────────────────────────────────────────────────────────────────

const solutionCards = [
  {
    title: "Brain-First Lock",
    text: "The AI tutor stays locked until your child has tried. When it does unlock, it cannot write code. It can only ask guiding questions. Independent thinking is measured and shown in your parent dashboard as a Brain Score.",
  },
  {
    title: "Conscious screen design",
    text: "Session limits by age, calibrated from American Academy of Pediatrics guidance. Mandatory 20-minute eye breaks. Warm amber palette instead of blue-dominant screens that suppress melatonin. These are architectural constraints, not optional settings.",
  },
  {
    title: "Offline by design",
    text: "When a session ends, SproutCode transitions to a full-screen nature view and suggests an age-matched offline activity. Nature, active, creative, or reading. Kids earn offline badges alongside coding XP. The platform is designed to send your child away from the screen.",
  },
];

function Solution() {
  const mobile = useIsMobile();
  return (
    <section id="how-it-works" style={{ background: "#052e16" }}>
      <div style={sectionPad}>
        <h2 style={{ ...heading, fontSize: mobile ? 28 : 38, color: "#fff", textAlign: "center", margin: "0 0 52px" }}>
          Three architectural commitments
        </h2>
        <div style={{
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "repeat(3, 1fr)",
          gap: 24,
        }}>
          {solutionCards.map(card => (
            <div key={card.title} style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 16, padding: "32px 28px",
            }}>
              <h3 style={{ ...heading, fontSize: 20, color: "#fff", margin: "0 0 14px", fontWeight: 700 }}>
                {card.title}
              </h3>
              <p style={{ ...body, fontSize: 15, color: "#a7f3d0", margin: 0 }}>
                {card.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── TRACKS ───────────────────────────────────────────────────────────────────

const trackCards = [
  {
    emoji: "🚀",
    name: "Explorers",
    age: "Ages 5 to 8",
    text: "Visual lessons with audio guidance. No reading required. Real Python concepts learned through animation and voice. The only coding platform built for pre-readers.",
  },
  {
    emoji: "⚙️",
    name: "Builders",
    age: "Ages 9 to 12",
    text: "Real Python. Loops, conditionals, functions. Brain-First Lock active. AI guides, never writes the code.",
  },
  {
    emoji: "💡",
    name: "Innovators",
    age: "Ages 13 to 17",
    text: "Python intermediate. Functions, APIs, prompt engineering. Built for the era they are actually growing up in.",
  },
];

function Tracks() {
  const mobile = useIsMobile();
  return (
    <section style={{ background: "#F5F5F4" }}>
      <div style={sectionPad}>
        <h2 style={{ ...heading, fontSize: mobile ? 28 : 38, color: "#1C1917", textAlign: "center", margin: "0 0 52px" }}>
          Three tracks. Built for how kids actually grow.
        </h2>
        <div style={{
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "repeat(3, 1fr)",
          gap: 24,
        }}>
          {trackCards.map(card => (
            <div key={card.name} style={{
              background: "#FFFFFF",
              border: "1px solid #E7E5E4",
              borderRadius: 16, padding: "32px 28px",
              boxShadow: "0 2px 20px rgba(0,0,0,0.05)",
            }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>{card.emoji}</div>
              <h3 style={{ ...heading, fontSize: 22, color: "#052e16", margin: 0, fontWeight: 700 }}>
                {card.name}
              </h3>
              <p style={{
                ...body,
                fontSize: 14, color: "#78716C", fontWeight: 600,
                margin: "0 0 16px",
                letterSpacing: 0.5, textTransform: "uppercase",
              }}>
                {card.age}
              </p>
              <p style={{ ...body, fontSize: 15, color: "#44403C", margin: 0 }}>
                {card.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── SOCIAL PROOF ─────────────────────────────────────────────────────────────

const quotes = [
  {
    text: "My 5 year old loved the first lesson, then went and built a LEGO set and drew a full schematic so the whole family could follow it.",
    attribution: "Beta parent, Australia",
  },
  {
    text: "He finished the lesson and immediately asked when more were being released.",
    attribution: "Beta parent, Australia",
  },
];

function SocialProof() {
  const mobile = useIsMobile();
  return (
    <section style={{ background: "#FFFBEB" }}>
      <div style={sectionPadNarrow}>
        <h2 style={{ ...heading, fontSize: mobile ? 28 : 38, color: "#1C1917", textAlign: "center", margin: "0 0 52px" }}>
          What beta families are saying
        </h2>
        <div style={{
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "repeat(2, 1fr)",
          gap: 24,
        }}>
          {quotes.map((q, i) => (
            <div key={i} style={{
              background: "#fff",
              borderRadius: 16, padding: "32px 28px",
              boxShadow: "0 2px 20px rgba(0,0,0,0.05)",
              border: "1px solid #E7E5E4",
            }}>
              <p style={{ ...body, fontSize: 16, color: "#1C1917", margin: "0 0 20px", fontStyle: "italic" }}>
                "{q.text}"
              </p>
              <p style={{ ...body, fontSize: 14, color: "#78716C", margin: 0, fontWeight: 600 }}>
                {q.attribution}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FOUNDER ──────────────────────────────────────────────────────────────────

function Founder() {
  const mobile = useIsMobile();
  return (
    <section style={{ background: "#052e16" }}>
      <div style={{
        ...sectionPad,
        display: mobile ? "flex" : "grid",
        flexDirection: mobile ? "column" : undefined,
        gridTemplateColumns: mobile ? undefined : "1fr 1fr",
        gap: mobile ? 32 : 48,
        alignItems: "center",
      }}>
        {/* Image */}
        <img
          src="/tyson-founder.jpg"
          alt="Tyson Venables, founder of SproutCode"
          style={{
            width: "100%",
            maxWidth: mobile ? undefined : 400,
            aspectRatio: "1 / 1",
            objectFit: "cover",
            borderRadius: 16,
          }}
        />

        {/* Text */}
        <div>
          <h2 style={{ ...heading, fontSize: mobile ? 28 : 38, color: "#fff", fontWeight: 800, margin: "0 0 28px" }}>
            Built by a parent and developer for the world his kids will grow up in
          </h2>
          <p style={{ ...body, fontSize: mobile ? 16 : 17, color: "#a7f3d0", lineHeight: 1.7, margin: "0 0 18px" }}>
            I am a software developer and a parent. I saw clearly where the world is heading. My own kids will grow up in an era where AI is not a feature but the background of their daily lives. I did not want to protect them from that future. I wanted them to be fluent in it. To speak the language of the technology that will shape their lives, and to keep the qualities that no technology can replicate. Independent thinking, focus, curiosity, and presence in the real world.
          </p>
          <p style={{ ...body, fontSize: mobile ? 16 : 17, color: "#a7f3d0", lineHeight: 1.7, margin: "0 0 18px" }}>
            SproutCode is the platform I built for my own kids first, then opened up to every parent who recognises the same opportunity.
          </p>
          <div style={{ marginTop: 24 }}>
            <p style={{ ...body, fontSize: 16, color: "#fff", fontWeight: 700, margin: 0 }}>Tyson Venables</p>
            <p style={{ ...body, fontSize: 14, color: "#78716C", fontWeight: 500, margin: "4px 0 0" }}>Founder, SproutCode</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── WAITLIST ─────────────────────────────────────────────────────────────────

function WaitlistForm({ onEnterApp }) {
  const mobile = useIsMobile();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    const referralCode = generateReferralCode(firstName, lastName);
    let referredBy = "";
    try { referredBy = sessionStorage.getItem("sproutcode_referred_by") || ""; } catch {}
    try {
      const res = await fetch(
        "https://services.leadconnectorhq.com/hooks/cJ5ZGn9UhH8WiR2uUnjX/webhook-trigger/cdc78888-4ef7-44e5-ad17-3ee6fe1441c5",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName,
            lastName,
            email,
            ageGroup,
            source: "sproutcode-landing",
            referralCode,
            referredBy,
          }),
        }
      );
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
      try { localStorage.setItem("sproutcode_visited", "true"); } catch {}
      setTimeout(() => onEnterApp(), 2500);
    } catch {
      setStatus("error");
    }
  };

  const inputStyle = {
    ...body,
    width: "100%", boxSizing: "border-box",
    background: "#292524", color: "#fff",
    border: "1px solid #44403C", borderRadius: 10,
    padding: "14px 16px", fontSize: 15,
    outline: "none",
  };

  return (
    <section id="waitlist" style={{ background: "#1C1917" }}>
      <div style={{ ...sectionPadNarrow, maxWidth: 520 }}>
        <h2 style={{ ...heading, fontSize: mobile ? 28 : 38, color: "#fff", textAlign: "center", margin: "0 0 16px" }}>
          Be part of the first 100 families
        </h2>
        <p style={{ ...body, fontSize: 16, color: "#A8A29E", textAlign: "center", margin: "0 0 44px" }}>
          Free beta access. No credit card required. We will email you when your spot is ready.
        </p>

        {status === "success" ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <p style={{ ...body, fontSize: 18, color: "#fff", margin: "0 0 16px" }}>
              You are on the list. Taking you to SproutCode now...
            </p>
            <span style={{ fontSize: 28 }}>🌱</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <input
              type="text"
              required
              placeholder="Your first name"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              style={inputStyle}
            />
            <input
              type="text"
              required
              placeholder="Your last name"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              style={inputStyle}
            />
            <input
              type="email"
              required
              placeholder="Your email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={inputStyle}
            />
            <select
              required
              value={ageGroup}
              onChange={e => setAgeGroup(e.target.value)}
              style={{ ...inputStyle, color: ageGroup ? "#fff" : "#78716C", appearance: "none" }}
            >
              <option value="" disabled>Select your child's age group</option>
              <option value="5-8">5 to 8 years (Explorers)</option>
              <option value="9-12">9 to 12 years (Builders)</option>
              <option value="13-17">13 to 17 years (Innovators)</option>
              <option value="mixed">Multiple kids, mixed ages</option>
            </select>

            {status === "error" && (
              <p style={{ ...body, color: "#F59E0B", fontSize: 14, margin: 0, textAlign: "center" }}>
                Something went wrong. Please email us at hello@sproutcode.app
              </p>
            )}

            <button type="submit" disabled={status === "submitting"} style={{
              ...body,
              width: "100%", background: "#F59E0B", color: "#1C1917",
              border: "none", borderRadius: 10, padding: "16px",
              fontSize: 16, fontWeight: 700, cursor: status === "submitting" ? "default" : "pointer",
              opacity: status === "submitting" ? 0.7 : 1,
            }}>
              {status === "submitting" ? "Joining..." : "Join the waitlist"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────

function Footer() {
  const mobile = useIsMobile();
  const linkStyle = {
    ...body, color: "#78716C", fontSize: 14, textDecoration: "none", fontWeight: 500,
  };
  return (
    <footer style={{ background: "#1C1917", borderTop: "1px solid #292524" }}>
      <div style={{
        maxWidth: 1120, margin: "0 auto", padding: "40px 24px",
        display: "flex",
        flexDirection: mobile ? "column" : "row",
        alignItems: mobile ? "center" : "flex-start",
        justifyContent: "space-between",
        gap: mobile ? 28 : 0,
        textAlign: mobile ? "center" : "left",
      }}>
        {/* Left */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: mobile ? "center" : "flex-start" }}>
            <span style={{ fontSize: 18 }}>🌱</span>
            <span style={{ ...heading, fontSize: 16, color: "#fff", fontWeight: 700 }}>SproutCode</span>
          </div>
          <p style={{ ...body, fontSize: 13, color: "#78716C", margin: "6px 0 0" }}>Conscious technology for childhood.</p>
        </div>

        {/* Right */}
        <div style={{ display: "flex", gap: 24 }}>
          <a href="https://www.instagram.com/sproutcodeapp" target="_blank" rel="noopener noreferrer" style={linkStyle}>Instagram</a>
          <a href="https://www.youtube.com/@SproutCodeApp" target="_blank" rel="noopener noreferrer" style={linkStyle}>YouTube</a>
          <a href="https://www.linkedin.com/company/sprout-code-app/" target="_blank" rel="noopener noreferrer" style={linkStyle}>LinkedIn</a>
        </div>
      </div>

      <div style={{ textAlign: "center", padding: "0 24px 32px" }}>
        <p style={{ ...body, fontSize: 12, color: "#57534E", margin: 0 }}>&copy; 2026 SproutCode</p>
      </div>
    </footer>
  );
}

// ─── LANDING PAGE ─────────────────────────────────────────────────────────────

export default function Landing({ onEnterApp }) {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) sessionStorage.setItem("sproutcode_referred_by", ref);
    } catch {}
  }, []);

  return (
    <div style={{ ...body, background: "#1C1917" }}>
      <FontLoader />
      <Hero />
      <Problem />
      <Solution />
      <Tracks />
      <SocialProof />
      <Founder />
      <WaitlistForm onEnterApp={onEnterApp} />
      <Footer />
    </div>
  );
}
